import React from 'react';
import { Home, PackageSearch, Heart, ShoppingCart } from 'lucide-react';
import Header from './Header.jsx';
import Footer from './Footer.jsx';
import { categories, getCategoryBySlug } from './categoriesData';

/**
 * زخرفة SVG مرسومة بالكود (مش صورة مجلوبة من الإنترنت) بتتلون تلقائيًا
 * بنفس لون الفئة، بتديها طابع بصري مميز في الهيدر من غير الاعتماد على صور خارجية.
 */
const CategoryIllustration = ({ colorClass = 'text-[#10B981]' }) => (
  <svg
    viewBox="0 0 400 300"
    xmlns="http://www.w3.org/2000/svg"
    className={`h-full w-full ${colorClass}`}
    aria-hidden="true"
  >
    <circle cx="230" cy="150" r="130" fill="currentColor" opacity="0.08" />
    <circle cx="230" cy="150" r="90" fill="currentColor" opacity="0.1" />

    <g transform="translate(140 90) rotate(-25)">
      <rect x="0" y="0" width="120" height="46" rx="23" fill="currentColor" opacity="0.9" />
      <path d="M60 0 H97 A23 23 0 0 1 97 46 H60 Z" fill="white" opacity="0.9" />
    </g>

    <g transform="translate(90 190) rotate(15)">
      <rect x="0" y="0" width="64" height="26" rx="13" fill="currentColor" opacity="0.55" />
      <path d="M32 0 H51 A13 13 0 0 1 51 26 H32 Z" fill="white" opacity="0.9" />
    </g>

    <path
      d="M40 230 H110 L130 195 L155 250 L175 210 L190 230 H260"
      fill="none"
      stroke="currentColor"
      strokeWidth="5"
      strokeLinecap="round"
      strokeLinejoin="round"
      opacity="0.6"
    />

    <g transform="translate(275 70)">
      <rect x="14" y="0" width="12" height="40" rx="4" fill="currentColor" opacity="0.75" />
      <rect x="0" y="14" width="40" height="12" rx="4" fill="currentColor" opacity="0.75" />
    </g>

    <circle cx="70" cy="70" r="6" fill="currentColor" opacity="0.4" />
    <circle cx="310" cy="200" r="8" fill="currentColor" opacity="0.35" />
    <circle cx="320" cy="130" r="4" fill="currentColor" opacity="0.5" />
  </svg>
);

/**
 * بطاقة منتج مبسطة تُستخدم داخل صفحة الفئة، بتدعم الإضافة للسلة والمفضلة.
 */
const ProductCard = ({ product, addToCart, addToFavorites, favorites = [] }) => {
  const isFavorite = favorites.some((item) => item.id === product.id);

  return (
    <div className="group rounded-2xl bg-white p-5 shadow-md ring-1 ring-gray-100 transition-all hover:-translate-y-1 hover:shadow-lg">
      <div className="mb-4 flex h-40 items-center justify-center rounded-xl bg-gray-50">
        {product.image ? (
          <img src={product.image} alt={product.name} className="h-full w-full rounded-xl object-cover" />
        ) : (
          <PackageSearch className="h-10 w-10 text-gray-300" />
        )}
      </div>
      <h3 className="mb-1 font-bold text-gray-900">{product.name}</h3>
      {product.price && (
        <p className="mb-4 text-lg font-bold text-[#10B981]">{product.price} ج.م</p>
      )}
      <div className="flex items-center gap-2">
        <button
          onClick={() => addToCart && addToCart(product)}
          className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#10B981] px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#0ea173]"
        >
          <ShoppingCart className="h-4 w-4" />
          أضف للسلة
        </button>
        <button
          onClick={() => addToFavorites && addToFavorites(product)}
          className={`rounded-xl p-2.5 ring-1 transition-colors ${
            isFavorite ? 'bg-red-50 text-red-500 ring-red-200' : 'text-gray-400 ring-gray-200 hover:text-red-500'
          }`}
        >
          <Heart className="h-4 w-4" fill={isFavorite ? 'currentColor' : 'none'} />
        </button>
      </div>
    </div>
  );
};

/**
 * صفحة مستقلة تُبنى ديناميكيًا لأي فئة بناءً على الـ slug بتاعها.
 * بدل ما نعمل ملف صفحة منفصل ومكرر لكل فئة، الصفحة دي بتاخد بيانات
 * الفئة من categoriesData.jsx وتعرض هيدر خاص بالفئة + منتجاتها.
 * الصفحة بتجيب الـ Header والـ Footer بتاعها بنفسها علشان تفضل ظاهرة
 * في كل صفحات الفئات (مش بس الصفحة الرئيسية).
 *
 * الاستخدام في App.jsx:
 *   <CategoryPage
 *     slug={currentPage}
 *     totalItems={totalItemsInCart}
 *     totalFavorites={favorites.length}
 *     setCurrentPage={setCurrentPage}
 *     addToCart={addToCart}
 *     addToFavorites={addToFavorites}
 *     favorites={favorites}
 *   />
 */
const CategoryPage = ({
  slug,
  totalItems = 0,
  totalFavorites = 0,
  setCurrentPage,
  addToCart,
  addToFavorites,
  favorites = [],
  products = []
}) => {
  const category = getCategoryBySlug(slug);

  if (!category) {
    return (
      <div className="font-['Cairo']">
        <Header totalItems={totalItems} totalFavorites={totalFavorites} setCurrentPage={setCurrentPage} />
        <section className="bg-white py-24 text-center">
          <p className="text-lg text-gray-600">عذرًا، هذه الفئة غير موجودة.</p>
          <button
            onClick={() => setCurrentPage && setCurrentPage('home')}
            className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#10B981] px-6 py-3 font-bold text-white hover:bg-[#0ea173]"
          >
            <Home className="h-4 w-4" />
            العودة للرئيسية
          </button>
        </section>
        <Footer setCurrentPage={setCurrentPage} />
      </div>
    );
  }

  const otherCategories = categories.filter((cat) => cat.page !== category.page).slice(0, 8);

  return (
    <div className="font-['Cairo']">
      <Header totalItems={totalItems} totalFavorites={totalFavorites} setCurrentPage={setCurrentPage} />

      {/* هيدر الفئة */}
      <section className={`${category.bgColor} py-12 lg:py-16`}>
        <div className="container mx-auto px-4">
          <div className="grid items-center gap-8 lg:grid-cols-[1.3fr_1fr]">
            <div className="flex flex-col items-start gap-6 sm:flex-row sm:items-center">
              <div className={`w-fit rounded-2xl bg-white p-5 shadow-sm ${category.color}`}>
                {category.icon}
              </div>
              <div>
                <h1 className="mb-2 text-3xl font-bold text-gray-900 lg:text-4xl">{category.name}</h1>
                <p className="mb-2 max-w-xl text-lg text-gray-600">{category.description}</p>
                <span className="inline-block rounded-full bg-white px-4 py-1.5 text-sm font-semibold text-gray-700 shadow-sm">
                  {category.count}
                </span>
              </div>
            </div>

            <div className="hidden h-48 w-full lg:block">
              <CategoryIllustration colorClass={category.color} />
            </div>
          </div>
        </div>
      </section>

      {/* منطقة المنتجات */}
      <section className="bg-white py-16 lg:py-20">
        <div className="container mx-auto px-4">
          {products.length > 0 ? (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {products.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  addToCart={addToCart}
                  addToFavorites={addToFavorites}
                  favorites={favorites}
                />
              ))}
            </div>
          ) : (
            <div className="mx-auto flex max-w-md flex-col items-center rounded-2xl bg-gray-50 px-6 py-16 text-center ring-1 ring-gray-100">
              <div className={`mb-5 rounded-2xl ${category.bgColor} p-5 ${category.color}`}>
                <PackageSearch className="h-10 w-10" />
              </div>
              <h3 className="mb-2 text-xl font-bold text-gray-900">منتجات هذه الفئة قريباً</h3>
              <p className="mb-6 text-gray-600">
                جاري تجهيز أفضل منتجات "{category.name}" لك، تابعنا فالمنتجات هتضاف قريبًا.
              </p>
              <button
                onClick={() => setCurrentPage && setCurrentPage('home')}
                className="rounded-xl bg-[#10B981] px-6 py-3 font-bold text-white transition-all hover:bg-[#0ea173]"
              >
                تصفح كل المنتجات
              </button>
            </div>
          )}
        </div>
      </section>

      {/* فئات أخرى قد تهمك */}
      <section className="bg-gray-50 py-14">
        <div className="container mx-auto px-4">
          <h2 className="mb-6 text-xl font-bold text-gray-900">فئات أخرى قد تهمك</h2>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {otherCategories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setCurrentPage && setCurrentPage(cat.page)}
                className="flex flex-col items-center gap-3 rounded-2xl bg-white p-5 text-center shadow-sm ring-1 ring-gray-100 transition-all hover:-translate-y-1 hover:shadow-md hover:ring-[#10B981]/20"
              >
                <div className={`rounded-xl ${cat.bgColor} p-3 ${cat.color}`}>{cat.icon}</div>
                <span className="text-sm font-semibold text-gray-700">{cat.name}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      <Footer setCurrentPage={setCurrentPage} />
    </div>
  );
};

export default CategoryPage;
